import type { FC, ReactNode } from 'react'
import type { WorkItem, WorkLayout } from '@/types'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import { TONE_SURFACES } from '@/lib/tones'

interface WorkCardProps {
    item: WorkItem
}

interface LayoutStyle {
    card: string
    media: string
    body: string
    sizes: string
}

// Grid placement per card shape. Row one pairs a wide and a tall card at the same height.
const LAYOUTS: Record<WorkLayout, LayoutStyle> = {
    feature: {
        card: 'md:col-span-2 lg:col-span-8',
        media: 'aspect-[4/3] sm:aspect-[3/2] lg:aspect-auto lg:h-[clamp(24rem,36vw,36rem)]',
        body: '',
        sizes: '(min-width: 64rem) 62vw, 94vw'
    },
    tall: {
        card: 'lg:col-span-4',
        media: 'aspect-[4/5] lg:aspect-auto lg:h-[clamp(24rem,36vw,36rem)]',
        body: '',
        sizes: '(min-width: 64rem) 31vw, (min-width: 48rem) 47vw, 94vw'
    },
    standard: {
        card: 'lg:col-span-4',
        media: 'aspect-[4/5]',
        body: '',
        sizes: '(min-width: 64rem) 31vw, (min-width: 48rem) 47vw, 94vw'
    },
    wide: {
        card: 'md:col-span-2 lg:col-span-12 lg:grid lg:grid-cols-12 lg:gap-3',
        media: 'aspect-[16/10] lg:col-span-7 lg:aspect-auto lg:min-h-[30rem]',
        body: 'lg:col-span-5 lg:justify-center lg:px-8 lg:py-8',
        sizes: '(min-width: 64rem) 56vw, 94vw'
    }
}

const WorkCard: FC<WorkCardProps> = ({ item }): ReactNode => {
    const layout = LAYOUTS[item.layout]

    return (
        <li className={cn('reveal flex flex-col rounded-[2rem] p-2.5 md:p-3', TONE_SURFACES[item.tone], layout.card)}>
            <div className={cn('relative overflow-hidden rounded-[1.5rem] bg-foreground/10', layout.media)}>
                <Image
                    src={item.image}
                    alt={item.imageAlt}
                    fill
                    sizes={layout.sizes}
                    className='object-cover'
                />

                <span className='absolute top-3 left-3 rounded-full bg-background px-3 py-1.5 font-mono text-[0.7rem] font-medium tracking-[0.16em] text-foreground uppercase md:top-4 md:left-4'>
                    {item.year}
                </span>
            </div>

            <div className={cn('flex flex-1 flex-col gap-5 px-3 pt-6 pb-3 md:px-4 md:pb-4', layout.body)}>
                <div>
                    <p className='font-mono text-xs font-medium tracking-[0.18em] uppercase'>
                        {item.category}
                    </p>

                    <h3 className='mt-2 font-heading text-[clamp(2.25rem,4vw,3.75rem)] leading-[0.88] font-black uppercase wdth-80'>
                        {item.client}
                    </h3>
                </div>

                <p className='flex items-end gap-3 border-y border-current/25 py-4'>
                    <span className='font-heading text-[clamp(2.5rem,4vw,3.5rem)] leading-[0.85] font-black wdth-75'>
                        {item.result.value}
                    </span>

                    <span className='pb-0.5 text-sm leading-snug font-semibold'>
                        {item.result.label}
                    </span>
                </p>

                <p className='max-w-xl text-base leading-relaxed'>
                    {item.summary}
                </p>

                <ul aria-label='Deliverables' className='mt-auto flex flex-wrap gap-2 pt-1'>
                    {item.deliverables.map(deliverable => (
                        <li
                            key={deliverable}
                            className='rounded-full border-[1.5px] border-current px-3 py-1 text-xs font-bold tracking-[0.08em] uppercase'
                        >
                            {deliverable}
                        </li>
                    ))}
                </ul>
            </div>
        </li>
    )
}

export default WorkCard
